import { getLocale } from 'i18n';
import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState } from 'react'
import styles from './DocumentView.module.scss'
import { signDocument, returnDocument, cancelDocument, sendAcquaintance, markAcquaintanceRead } from '../../../../service/DocflowService'

function fmtDate(str) {
  if (!str) return null
  const d = new Date(str)
  if (isNaN(d.getTime())) return str
  return d.toLocaleString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const SIG_STATUS_COLORS = {
  pending:  { color: "var(--theme-orange-text, #854d0e)", bg: "var(--theme-warning-soft, #fef9c3)" },
  signed:   { color: "var(--theme-success-text, #15803d)", bg: "var(--theme-success-soft, #dcfce7)" },
  returned: { color: "var(--theme-danger-text, #b91c1c)", bg: "var(--theme-danger-soft, #fee2e2)" },
}
const SIG_ICONS = { pending: '⏳', signed: '✅', returned: '↩️' }

function ParticipantCard({ label, name, dept, position, highlight }) {
  useInterfaceLanguage();
  if (!name) return null
  return (
    <div className={`${styles.participantCard} ${highlight ? styles.participantHighlight : ''}`}>
      <span className={styles.participantLabel}>{tr(label)}</span>
      <span className={styles.participantName}>{tr(name)}</span>
      {tr((position || dept) && (
        <span className={styles.participantMeta}>{[position, dept].filter(Boolean).join(' · ')}</span>
      ))}
    </div>
  )
}

export default function DocActions({ doc, myRole, onUpdated, employees = [], onEdit }) {
  useInterfaceLanguage();
  const [returnOpen, setReturnOpen]     = useState(false)
  const [reason, setReason]             = useState('')
  const [acqOpen, setAcqOpen]           = useState(false)
  const [acqNote, setAcqNote]           = useState('')
  const [acqSearch, setAcqSearch]       = useState('')
  const [acqIds, setAcqIds]             = useState([])
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [loading, setLoading]           = useState(false)

  // signer acts only on 'signing'; resolution acts on 'signing' (all signers done) or 'pending' (no signers)
  const canSign   = (myRole === 'signer' && doc.status === 'signing')
                 || (myRole === 'resolution' && (doc.status === 'signing' || doc.status === 'pending'))
  const canReturn = canSign
  const canAcq    = (myRole === 'resolution' || myRole === 'creator') && doc.status === 'approved'
  const canCancel = myRole === 'creator' && (doc.status === 'pending' || doc.status === 'signing')
  const canEdit   = myRole === 'creator' && (doc.status === 'returned' || doc.status === 'draft')

  async function handleMarkRead() {
    setLoading(true)
    try { const r = await markAcquaintanceRead(doc.id); onUpdated(r.data) }
    finally { setLoading(false) }
  }

  async function handleCancelDoc() {
    setLoading(true)
    try { const r = await cancelDocument(doc.id); onUpdated(r.data) }
    finally { setLoading(false); setCancelConfirm(false) }
  }

  async function handleSign() {
    setLoading(true)
    try { const r = await signDocument(doc.id); onUpdated(r.data) }
    finally { setLoading(false) }
  }

  async function handleReturn() {
    if (!reason.trim()) return
    setLoading(true)
    try {
      const r = await returnDocument(doc.id, reason)
      onUpdated(r.data); setReturnOpen(false); setReason('')
    } finally { setLoading(false) }
  }

  async function handleSendAcq() {
    if (!acqIds.length) return
    setLoading(true)
    try {
      await sendAcquaintance(doc.id, { employee_ids: acqIds, note: acqNote })
      setAcqOpen(false); setAcqIds([]); setAcqNote(''); setAcqSearch('')
    } finally { setLoading(false) }
  }

  function toggleEmp(id) {
    setAcqIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id])
  }

  const filteredEmps = acqSearch
    ? employees.filter(e => e.full_name.toLowerCase().includes(acqSearch.toLowerCase()))
    : employees

  const ROLE_INFO = {
    creator:    { icon: '✏️', label: "Вы — отправитель документа" },
    signer:     { icon: '✍️', label: "Вы — текущий подписант" },
    resolution: { icon: '🔖', label: "Вы — ответственный за резолюцию" },
    viewer:     { icon: '👁️', label: "Вы просматриваете документ" },
  }
  const roleInfo = ROLE_INFO[myRole] || ROLE_INFO.viewer

  return (
    <div className={styles.actionsSection}>

      {/* ── Участники ── */}
      <div className={styles.participantsBlock}>
        <ParticipantCard
          label={tr("Отправитель")}
          name={doc.from_employee_name}
          dept={doc.from_employee_dept}
          position={doc.from_employee_position}
          highlight={myRole === 'creator'}
        />
        <ParticipantCard
          label={tr("Получатель")}
          name={doc.to_employee_name}
          dept={doc.to_employee_dept}
          position={doc.to_employee_position}
        />
        {tr(doc.resolution_name && (
          <ParticipantCard
            label={tr("Ответственный (Резолюция)")}
            name={doc.resolution_name}
            dept={doc.resolution_dept}
            position={doc.resolution_position}
            highlight={myRole === 'resolution'}
          />
        ))}
      </div>

      {/* ── Цепочка подписания ── */}
      {tr(doc.signers && doc.signers.length > 0 && (
        <div className={styles.signersBlock}>
          <div className={styles.signersSectionTitle}>{tr("✍️ Цепочка подписания")}</div>
          <ol className={styles.sigChain}>
            {doc.signers.map(s => {
              const sc = SIG_STATUS_COLORS[s.status] || SIG_STATUS_COLORS.pending
              const isCurrent = s.order === doc.current_signer_order && s.status === 'pending'
              return (
                <li key={s.id} className={`${styles.sigItem} ${isCurrent ? styles.sigCurrent : ''}`}>
                  <div className={styles.sigOrder}>
                    <span>{tr(s.order)}</span>
                    {tr(isCurrent && <span className={styles.currentDot} />)}
                  </div>
                  <div className={styles.sigInfo}>
                    <span className={styles.sigName}>{tr(s.employee_name)}</span>
                    <span className={styles.sigMeta}>
                      {[s.employee_position, s.employee_dept].filter(Boolean).join(' · ')}
                    </span>
                    {tr(s.status === 'signed' && s.signed_at && (
                      <span className={styles.sigDate}>{tr("Подписано ")}{fmtDate(s.signed_at)}</span>
                    ))}
                    {tr(s.status === 'signed' && s.signature_key && (
                      <span className={styles.sigKey} title={tr("Ключ подписи")}>🔑 {tr(s.signature_key)}</span>
                    ))}
                    {tr(s.status === 'returned' && s.return_reason && (
                      <span className={styles.sigReturnReason}>{tr("Причина: ")}{tr(s.return_reason)}</span>
                    ))}
                  </div>
                  <span className={styles.sigBadge} style={{ background: sc.bg, color: sc.color }}>
                    {tr(SIG_ICONS[s.status])} {tr(s.status_display)}
                  </span>
                </li>
              )
            })}
          </ol>
        </div>
      ))}

      {/* ── Моя роль ── */}
      <div className={styles.roleTag}>
        <span>{tr(roleInfo.icon)}</span>
        {tr(roleInfo.label)}
      </div>

      {/* ── Creator actions ── */}
      {tr(canEdit && (
        <button className={styles.signBtn} onClick={onEdit} disabled={loading}>{tr("✏️ Доработать и повторно отправить")}</button>
      ))}
      {tr(canCancel && !cancelConfirm && (
        <button className={styles.returnBtnTrigger} onClick={() => setCancelConfirm(true)} disabled={loading}>{tr("🚫 Отозвать документ")}</button>
      ))}
      {tr(cancelConfirm && (
        <div className={styles.returnForm}>
          <p className={styles.returnLabel}>{tr("Вы уверены, что хотите отозвать документ?")}</p>
          <div className={styles.returnBtns}>
            <button type="button" className={styles.cancelReturnBtn}
              onClick={() => setCancelConfirm(false)}>{tr("Нет, оставить")}</button>
            <button type="button" className={styles.confirmReturnBtn}
              onClick={handleCancelDoc} disabled={loading}>
              {loading ? tr("Отзыв…") : tr("Да, отозвать")}
            </button>
          </div>
        </div>
      ))}

      {/* ── Кнопка: Подписать ── */}
      {tr(canSign && !returnOpen && (
        <button className={styles.signBtn} onClick={handleSign} disabled={loading}>
          {loading ? tr("Подписание…") : tr("✅ Подписать документ")}
        </button>
      ))}

      {/* ── Кнопка: Вернуть ── */}
      {tr(canReturn && !returnOpen && (
        <button className={styles.returnBtnTrigger} onClick={() => setReturnOpen(true)}>{tr("↩️ Вернуть на доработку")}</button>
      ))}

      {/* ── Форма возврата ── */}
      {tr(returnOpen && (
        <div className={styles.returnForm}>
          <label className={styles.returnLabel}>{tr("Причина возврата")}</label>
          <textarea
            className={styles.returnTextarea}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={tr("Опишите причину возврата…")}
            rows={3}
            autoFocus
          />
          <div className={styles.returnBtns}>
            <button type="button" className={styles.cancelReturnBtn}
              onClick={() => { setReturnOpen(false); setReason('') }}>{tr("Отмена")}</button>
            <button type="button" className={styles.confirmReturnBtn}
              onClick={handleReturn} disabled={!reason.trim() || loading}>
              {loading ? tr("Отправка…") : tr("Подтвердить возврат")}
            </button>
          </div>
        </div>
      ))}

      {/* ── Кнопка: Отправить на ознакомление ── */}
      {tr(canAcq && !acqOpen && (
        <button className={styles.acqBtn} onClick={() => setAcqOpen(true)}>{tr("📤 Отправить на ознакомление")}</button>
      ))}

      {/* ── Форма ознакомления ── */}
      {tr(acqOpen && (
        <div className={styles.acqFormPanel}>
          <div className={styles.acqFormHeader}>
            <span className={styles.acqFormTitle}>{tr("📤 Ознакомление")}</span>
            <button type="button" className={styles.acqCloseBtn}
              onClick={() => { setAcqOpen(false); setAcqIds([]); setAcqNote(''); setAcqSearch('') }}>
              ×
            </button>
          </div>

          <input
            type="text"
            className={styles.acqSearchInput}
            placeholder={tr("Поиск сотрудников…")}
            value={acqSearch}
            onChange={e => setAcqSearch(e.target.value)}
          />

          <div className={styles.acqEmpList}>
            {tr(filteredEmps.length === 0 && (
              <p className={styles.emptySub}>{tr("Сотрудники не найдены")}</p>
            ))}
            {filteredEmps.map(e => (
              <label
                key={e.id}
                className={`${styles.acqEmpItem} ${acqIds.includes(e.id) ? styles.acqEmpSelected : ''}`}
              >
                <input
                  type="checkbox"
                  checked={acqIds.includes(e.id)}
                  onChange={() => toggleEmp(e.id)}
                  className={styles.acqCheckbox}
                />
                <div className={styles.acqEmpInfo}>
                  <span className={styles.acqEmpName}>{tr(e.full_name)}</span>
                  {tr(e.department && <span className={styles.acqEmpMeta}>{tr(e.department)}</span>)}
                </div>
              </label>
            ))}
          </div>

          {tr(acqIds.length > 0 && (
            <div className={styles.acqSelectedBadge}>{tr("Выбрано: ")}{tr(acqIds.length)}</div>
          ))}

          <textarea
            className={styles.returnTextarea}
            value={acqNote}
            onChange={e => setAcqNote(e.target.value)}
            placeholder={tr("Примечание (необязательно)…")}
            rows={2}
          />

          <button
            type="button"
            className={styles.signBtn}
            onClick={handleSendAcq}
            disabled={!acqIds.length || loading}
          >
            {loading ? tr("Отправка…") : tr("📤 Отправить ({{value0}})", { value0: acqIds.length })}
          </button>
        </div>
      ))}

      {/* ── Статус-подсказка ── */}
      {tr(!canSign && !canAcq && !canCancel && !canEdit && !returnOpen && !acqOpen && myRole === 'viewer' && (
        <p className={styles.noActionHint}>
          {tr(doc.status === 'approved'  && tr("✅ Документ утверждён."))}
          {tr(doc.status === 'cancelled' && tr("🚫 Документ отменён."))}
          {tr(doc.status === 'pending'   && tr("⏳ Документ ожидает обработки."))}
          {tr(doc.status === 'signing'   && tr("⏳ Документ на подписании."))}
        </p>
      ))}

      {/* ── Ознакомление: показываем отправленные партии ── */}
      {tr((doc.acquaintances || []).length > 0 && (
        <div className={styles.signersBlock} style={{ marginTop: 16 }}>
          <div className={styles.signersSectionTitle}>{tr("📮 Ознакомление")}</div>
          {doc.acquaintances.map(acq => (
            <div key={acq.id} style={{ marginBottom: 12 }}>
              {tr(acq.note && (
                <p style={{ fontSize: 12, color: "var(--theme-text-muted, #64748b)", marginBottom: 6 }}>
                  📝 {tr(acq.note)}
                </p>
              ))}
              <ol className={styles.sigChain}>
                {(acq.people || []).map(p => (
                  <li key={p.employee_id} className={styles.sigItem}>
                    <div className={styles.sigInfo}>
                      <span className={styles.sigName}>{tr(p.employee_name)}</span>
                      <span className={styles.sigMeta}>
                        {[p.employee_pos, p.employee_dept].filter(Boolean).join(' · ')}
                      </span>
                      {tr(p.is_read && p.read_at && (
                        <span className={styles.sigDate}>{tr("Ознакомлен ")}{fmtDate(p.read_at)}</span>
                      ))}
                    </div>
                    <span
                      className={styles.sigBadge}
                      style={p.is_read
                        ? { background: "var(--theme-success-soft, #dcfce7)", color: "var(--theme-success-text, #15803d)" }
                        : { background: "var(--theme-warning-soft, #fef9c3)", color: "var(--theme-orange-text, #854d0e)" }}
                    >
                      {p.is_read ? tr("✅ Ознакомлен") : tr("⏳ Ожидает")}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      ))}

      {/* ── Кнопка «Ознакомиться» — для участника ознакомления ── */}
      {tr(myRole === 'viewer' && doc.status === 'approved' &&
       (doc.acquaintances || []).some(acq => acq.people?.some(p => !p.is_read)) && (
        <button
          className={styles.signBtn}
          onClick={handleMarkRead}
          disabled={loading}
          style={{ marginTop: 8 }}
        >
          {loading ? tr("Отправка…") : tr("✅ Ознакомиться")}
        </button>
      ))}
    </div>
  )
}
